/**
 * The cover page and the sign-in gate.
 *
 * A faithful port of the single-file demo's gate: the same five stages
 * (first run, credentials, authenticator enrolment, code, recovery), the same
 * validation and the same wording, with the animated approach-lit runway
 * running on the left and the security check sitting under the card so nobody
 * mistakes this for protection of the data behind it.
 *
 * Three layers, as before — a password, a time-based code, and whether the
 * device is one the account has seen. The device check is not a challenge; it
 * is recorded and reported, which is all a client-side gate can honestly do.
 */
import { Fragment, useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { t } from "../../i18n";
import { drawGateArt } from "../canvas/gateArt";
import { GO } from "../lib/canvas";
import {
  accounts,
  checkPassword,
  createAccount,
  deviceId,
  recoveryValid,
  saveAccounts,
  startSession,
  type Account,
  type DemoUser,
} from "../auth/accounts";
import { cryptoReady, rnd, toB32, totpDrift, totpValid } from "../auth/crypto";
import { securityCheck } from "../auth/securityCheck";
import { AuthenticatorQr, otpauthUri } from "./AuthenticatorQr";

/* ─────────────────────────────────────────────────────────────────────────────
   THE GATE IS CLOSED.

   `DEV_OPEN` was true through the build: anyone who opened the app walked
   straight in — no email, no password, no authenticator code — with a red
   strip across every screen saying so. It was closed on 2026-09-16.

   What closing it means, stated plainly because the opposite claim is the
   easy one to make:

     · The first person to open a copy ENROLS: they set an email, a password
       and an authenticator, and they become the administrator of that copy.
       There is no default account and no back door, so nothing has to be
       changed after an install to be safe.
     · Every later sign-in needs the password AND a six-digit code.
     · Five wrong attempts lock the account for a minute, and the lock is on
       the account rather than on the form.
     · The session lives in sessionStorage and ends when the tab closes; it
       also ends after thirty idle minutes.

   And what it does NOT mean, which the security panel repeats on every
   screen: this is a gate in a browser. It stops somebody using this copy of
   the screen. It is not what protects the data — that is the platform's own
   authentication, and the access level it issues with it. A device with the
   accounts cleared is a device that enrols again, and that is inherent to a
   client-side gate rather than a defect in this one.

   Setting this back to true re-opens the door and brings the red strip back
   with it. It is here as a flag, not as a setting: it should be true only on
   a machine building the pages.
   ───────────────────────────────────────────────────────────────────────────── */
export const DEV_OPEN = false;

type Stage = "credentials" | "enrol" | "code" | "recovery" | "reset" | "forgot";

export interface LoginGateProps {
  /** Set when the gate is being looked at again from inside a live session. */
  peek?: boolean;
  /** Signed in — hand the session up. */
  onEnter: (user: DemoUser) => void;
  /** Cover dismissed without signing in or out (peek only). */
  onBack?: () => void;
  /** Sign out from the cover (peek only). */
  onSignOut?: () => void;
  /** Opens the full security panel. */
  onSecurityPanel?: () => void;
  auditCount?: number | null;
}

/**
 * A translated sentence with markup in it: each `{name}` in the text is
 * replaced by its node, so the sentence stays whole for the translator and the
 * bold or code stays where the language puts it.
 */
function rich(text: string, nodes: Record<string, ReactNode>): ReactNode {
  return text
    .split(/\{(\w+)\}/)
    .map((part, i) => <Fragment key={i}>{i % 2 ? (nodes[part] ?? `{${part}}`) : part}</Fragment>);
}

/** The animated cover art, redrawn every frame off Date.now(). */
function GateArt() {
  const ref = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    let raf = 0;
    const loop = () => {
      const c = ref.current;
      if (!c) return;
      drawGateArt(c);
      raf = requestAnimationFrame(loop);
    };
    loop();
    return () => cancelAnimationFrame(raf);
  }, []);
  return <canvas id="gateArt" ref={ref} />;
}

const FEATURES: [string, string][] = [
  ["Predictive reliability", "failure likelihood, expected counts and 95% intervals from this airport's own records"],
  [
    "Intelligent warning systems",
    "alarms that route themselves, anomalies ranked by consequence, and a warning before a thing fails rather than after",
  ],
  ["Safety management", "hazards, risk matrices and the four Annex 19 components"],
  [
    "Smart operational dashboards",
    "nineteen boards from the airfield to the money, graded against targets and drawn from the records themselves",
  ],
];

export function LoginGate({
  peek = false,
  onEnter,
  onBack,
  onSignOut,
  onSecurityPanel,
  auditCount = null,
}: LoginGateProps) {
  const [stage, setStage] = useState<Stage>("credentials");
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [pass2, setPass2] = useState("");
  const [role, setRole] = useState("");
  const [code, setCode] = useState("");
  const [msg, setMsg] = useState("");
  const [acc, setAcc] = useState<Account | null>(null);
  /* A new authenticator key being set up for an existing account. It replaces
     the old one only when a code from it is accepted, so a set-up abandoned
     half way never locks anybody out. */
  const [pendingSecret, setPendingSecret] = useState<string | null>(null);
  const [list, setList] = useState<Account[]>(() => accounts());
  const first = list.length === 0;
  const ready = cryptoReady();
  const S = securityCheck(DEV_OPEN, auditCount);

  /* The gate holds the page while it is up. */
  useEffect(() => {
    document.body.classList.add("locked");
    return () => document.body.classList.remove("locked");
  }, []);

  const focusRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    const timer = setTimeout(() => focusRef.current?.focus(), 60);
    return () => clearTimeout(timer);
  }, [stage, first]);

  const enter = useCallback(
    (a: Account) => {
      const { user } = startSession(a);
      onEnter(user);
    },
    [onEnter],
  );

  /* --- first run: create the administrator ---------------------------------- */
  const doCreate = async () => {
    const em = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(em)) return setMsg(t("That does not look like an email address."));
    if (pass.length < 12) return setMsg(t("Use twelve characters or more. Length beats cleverness."));
    if (pass !== pass2) return setMsg(t("The two passwords are not the same."));
    const a = await createAccount(em, pass, role.trim(), true);
    setAcc(a);
    setList(accounts());
    setStage("enrol");
    setMsg("");
  };

  /* --- credentials ----------------------------------------------------------- */
  /**
   * Has this account ever finished setting up an authenticator?
   *
   * `devices` is appended only in doVerify, after a code from the app has been
   * accepted — so an empty list means no app has ever produced a valid code
   * for this account, whatever `secret` holds.
   */
  const enrolled = (a: Account) => (a.devices || []).length > 0;

  const doNext = async () => {
    const em = email.trim();
    if (!em || !pass) return setMsg(t("Both fields are needed."));
    const r = await checkPassword(em, pass);
    if (!r.ok) return setMsg(r.why || "");
    setAcc(r.acc!);
    /*
     * A secret is generated when the account is created, so `secret` says
     * nothing about whether anybody ever scanned it. `devices` does: a device
     * is recorded only after a code from the app has been verified on it.
     *
     * Reading `secret` sent everyone who pressed SKIP THE SECOND FACTOR to a
     * screen asking for six digits from an app they never set up — and the
     * way out, SHOW THE BARCODE AGAIN, asked for the same six digits. The
     * password had already been proven a moment earlier and there was nothing
     * else to prove against, so the account was shut out of its own enrolment.
     * Never enrolled means enrol, not prove it.
     */
    setStage(enrolled(r.acc!) ? "code" : "enrol");
    setMsg("");
  };

  /**
   * Why six correctly typed digits were refused.
   *
   * "Not valid" named none of the three things that cause it, and two of them
   * are not the person's fault. A phone whose clock has drifted produces a
   * code that is right for the wrong minute; and scanning a new barcode
   * leaves the app holding two entries with the same name, nothing on the
   * phone saying which is which, and a one-in-two chance of reading the dead
   * one. Both are measurable from here, so say which it is.
   *
   * Nothing below accepts a code that `totpValid` has already refused.
   */
  const whyRefused = async (secret: string, c: string): Promise<string> => {
    const drift = await totpDrift(secret, c);
    if (drift !== null) {
      const off = Math.abs(drift) * 30;
      /* Two whole sentences rather than one with a {dir} hole in it: a
         single loose word is a translation key that collides with every
         other dictionary, and Arabic does not agree with English about
         where in the sentence that word belongs. */
      return drift < 0
        ? t(
            "Those are the right six digits, but for a different moment: the clock on the device running the app is about {n} seconds behind. Set that device to get the time automatically, then read the next code.",
            { n: off },
          )
        : t(
            "Those are the right six digits, but for a different moment: the clock on the device running the app is about {n} seconds ahead. Set that device to get the time automatically, then read the next code.",
            { n: off },
          );
    }
    const stored = accounts().find((x) => x.email === acc?.email);
    if (stored && stored.secret !== secret && (await totpDrift(stored.secret, c)) !== null)
      return t(
        "That is a code from the barcode this one replaces. The app now holds two entries called Sky Logics — delete the older one, and read the code from the entry added just now.",
      );
    return t("That code is not valid. Check the clock on the device running the app.");
  };

  /* --- the six digits, then the device layer -------------------------------- */
  const doVerify = async () => {
    const c = code.trim();
    if (!/^\d{6}$/.test(c)) return setMsg(t("Six digits."));
    if (!acc) return;
    if (!(await totpValid(acc.secret, c))) return setMsg(await whyRefused(acc.secret, c));
    const dev = deviceId();
    const l = accounts();
    const a = l.find((x) => x.email === acc.email)!;
    if (pendingSecret) {
      a.secret = pendingSecret;
      saveAccounts(l);
      setPendingSecret(null);
    }
    const known = (a.devices || []).includes(dev);
    if (!known) {
      a.devices = (a.devices || []).concat(dev);
      saveAccounts(l);
    }
    enter(a);
  };

  const doSkip = () => {
    if (
      !confirm(
        t(
          "Sign in without a second factor?\n\nOne password is the whole gate then, and it is a gate inside the file it protects.",
        ),
      )
    )
      return;
    const l = accounts();
    const a = l.find((x) => x.email === acc!.email)!;
    enter(a);
  };

  const doRecovery = async () => {
    const c = code.trim();
    if (!c) return setMsg(t("Enter one of the recovery codes."));
    if (!(await recoveryValid(acc!.email, c)))
      return setMsg(t("That code is not on the list, or it has already been used."));
    const l = accounts();
    const a = l.find((x) => x.email === acc!.email)!;
    a.devices = (a.devices || []).concat(deviceId());
    saveAccounts(l);
    enter(a);
    setTimeout(
      () =>
        alert(
          t(
            "Signed in with a recovery code. {n} of the ten are left.\n\nTo set up the authenticator on a phone, sign in again and choose SHOW THE BARCODE AGAIN.",
            { n: (a.recovery || []).length },
          ),
        ),
      400,
    );
  };

  /* --- the barcode again: a new phone, or the app was never set up ---------- */
  const doReset = async () => {
    if (!acc) return;
    /*
     * Nothing to prove against. An account that has never verified a code on
     * any device has no working authenticator and may have no recovery code
     * either — asking for one is asking for something that cannot exist. The
     * password was proven on the previous step; show the barcode.
     */
    if (!enrolled(acc)) {
      const fresh = toB32(rnd(20));
      setPendingSecret(fresh);
      setAcc({ ...acc, secret: fresh, plainRecovery: undefined });
      setCode("");
      setMsg("");
      return setStage("enrol");
    }
    const c = code.trim();
    if (!c) return setMsg(t("Enter the current six-digit code or a recovery code."));
    const proven = /^\d{6}$/.test(c) ? await totpValid(acc.secret, c) : await recoveryValid(acc.email, c);
    if (!proven)
      return setMsg(
        /^\d{6}$/.test(c)
          ? await whyRefused(acc.secret, c)
          : t("That recovery code is not on the list, or it has already been spent."),
      );
    const secret = toB32(rnd(20));
    setPendingSecret(secret);
    setAcc({ ...acc, secret, plainRecovery: undefined });
    setCode("");
    setMsg("");
    setStage("enrol");
  };

  /* --- the app itself is gone ----------------------------------------------- */
  /**
   * The way out when there is no code to give and no recovery code left.
   *
   * It asks for the password a second time and then issues a fresh barcode.
   * That is a re-confirmation, not a second proof: the password was already
   * accepted one screen earlier, so this door is exactly as strong as the
   * password and no stronger, which is why the screen says so. It is not a
   * hole opened here either — the enrolment screen has always offered SKIP
   * THE SECOND FACTOR to anybody holding the password. What it removes is
   * the only state this gate had with no way out of it: enrolled, app lost,
   * recovery codes lost, and every screen asking for the one thing that
   * cannot be produced.
   *
   * The new secret is held, not saved, until a code from it is accepted, so
   * abandoning this half way leaves the old authenticator working.
   */
  const doForgot = async () => {
    if (!acc) return;
    if (!pass) return setMsg(t("Type the password for this account."));
    const r = await checkPassword(acc.email, pass);
    if (!r.ok) return setMsg(r.why || "");
    const fresh = toB32(rnd(20));
    setPendingSecret(fresh);
    setAcc({ ...r.acc!, secret: fresh, plainRecovery: undefined });
    setCode("");
    setMsg("");
    setStage("enrol");
  };

  const copy = async (text: string, ok: string, bad: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setMsg(ok);
    } catch {
      setMsg(bad);
    }
  };

  /* Enter submits whichever primary action the stage is showing. */
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    if (first) void doCreate();
    else if (stage === "credentials") void doNext();
    else if (stage === "recovery") void doRecovery();
    else if (stage === "reset") void doReset();
    else if (stage === "forgot") void doForgot();
    else void doVerify();
  };

  /* --- no Web Crypto: say so rather than offer theatre ---------------------- */
  if (!ready) {
    return (
      <div id="gate" className="on">
        <div id="gateBody">
          <div className="gwrap">
            <div className="gleft">
              <div className="gbrand">
                <img src="/assets/logo.png" alt="Sky Works Group Holdings" />
              </div>
              <h1>
                SKY LOGICS<span>INTELLIGENCE SYSTEMS</span>
              </h1>
            </div>
            <div className="gright">
              <div className="gcard">
                <div className="eyebrow">{t("Cannot set up the gate here")}</div>
                <h2>{t("This browser will not do the cryptography")}</h2>
                <p>
                  {rich(
                    t(
                      "Passwords are hashed and the second factor is calculated with Web Crypto, which browsers only provide in a secure context — served over HTTPS, on localhost, or opened as a file in a browser that allows it. This page is on {protocol}, where it is not available.",
                    ),
                    { protocol: <code>{location.protocol}</code> },
                  )}
                </p>
                <p>
                  {t(
                    "Serving the file over HTTPS fixes it, and is the same step that gives the platform real protection. Until then the sign-in screen would be theatre, so it is not offered.",
                  )}
                </p>
                <button className="btn primary" onClick={() => onBack?.()}>
                  {t("CONTINUE WITHOUT SIGNING IN")}
                </button>
                <button className="btn ghost" onClick={() => onSecurityPanel?.()}>
                  {t("READ THE SECURITY CHECK")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <GateFrame>
      {peek ? (
        <div className="gcard" style={{ borderTopColor: "#3FD07A" }}>
          <div className="eyebrow">{t("You are already in")}</div>
          <h2>{t("Nothing has stopped")}</h2>
          <p>
            {DEV_OPEN
              ? t("This is the cover, shown while the platform keeps running behind it. Development mode is on.")
              : t("This is the cover, shown while your session keeps running behind it.")}
          </p>
          <button className="btn primary" onClick={() => onBack?.()}>
            {t("BACK TO THE DASHBOARD")}
          </button>
          <button className="btn ghost" onClick={() => onSignOut?.()}>
            {t("SIGN OUT INSTEAD")}
          </button>
        </div>
      ) : DEV_OPEN ? (
        <div className="gcard gdev">
          <div className="eyebrow">{t("Development mode")}</div>
          <h2>{t("Sign-in is switched off")}</h2>
          <p>
            {t(
              "The credentials and the authenticator code are disabled while these pages are being built. Anyone opening this file walks straight in.",
            )}
          </p>
          <button className="btn primary" onClick={() => onEnter({ name: "Developer", role: "Development" })}>
            {t("ENTER THE PLATFORM")}
          </button>
          <div className="gdevnote">
            <b>{t("Before this goes to anybody")}</b>
            <span>
              {rich(
                t(
                  "Set {flag} near the top of {file}, rebuild, and the three layers come back exactly as they were — accounts, authenticator and device are all still there, untouched.",
                ),
                { flag: <code>DEV_OPEN = false</code>, file: <code>src/demo/gate/LoginGate.tsx</code> },
              )}
            </span>
          </div>
        </div>
      ) : first ? (
        <div className="gcard">
          <div className="eyebrow">{t("First run")}</div>
          <h2>{t("Set up the first account")}</h2>
          <p>{t("No account exists on this device yet. The first one is the administrator.")}</p>
          <label>
            {t("Email address")}
            <input
              type="email"
              ref={focusRef}
              autoComplete="username"
              placeholder="you@tibah.example"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={onKey}
            />
          </label>
          <label>
            {t("Password")}
            <input
              type="password"
              autoComplete="new-password"
              placeholder={t("twelve characters or more")}
              value={pass}
              onChange={(e) => setPass(e.target.value)}
              onKeyDown={onKey}
            />
          </label>
          <label>
            {t("Again")}
            <input
              type="password"
              autoComplete="new-password"
              value={pass2}
              onChange={(e) => setPass2(e.target.value)}
              onKeyDown={onKey}
            />
          </label>
          <label>
            {t("Your role")}
            <input
              type="text"
              placeholder={t("Aerodrome manager")}
              value={role}
              onChange={(e) => setRole(e.target.value)}
              onKeyDown={onKey}
            />
          </label>
          {msg && <div className="gmsg">{msg}</div>}
          <button className="btn primary" onClick={() => void doCreate()}>
            {t("CREATE THE ACCOUNT")}
          </button>
        </div>
      ) : stage === "credentials" ? (
        <div className="gcard">
          <div className="eyebrow">{t("Sign in")}</div>
          <h2>{t("Restricted system")}</h2>
          <p>
            {t(
              "For the people named on the operator's access list. Everything you do is recorded against your account.",
            )}
          </p>
          <label>
            {t("Email address")}
            <input
              type="email"
              ref={focusRef}
              autoComplete="username"
              placeholder="you@tibah.example"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={onKey}
            />
          </label>
          <label>
            {t("Password")}
            <input
              type="password"
              autoComplete="current-password"
              value={pass}
              onChange={(e) => setPass(e.target.value)}
              onKeyDown={onKey}
            />
          </label>
          {msg && <div className="gmsg">{msg}</div>}
          <button className="btn primary" onClick={() => void doNext()}>
            {t("CONTINUE")}
          </button>
          <div className="gsteps">
            <i className="on">{t("1 Credentials")}</i>
            <i>{t("2 Authenticator")}</i>
            <i>{t("3 Device")}</i>
          </div>
        </div>
      ) : stage === "enrol" ? (
        <div className="gcard">
          <div className="eyebrow">{t("Second factor · where the code comes from")}</div>
          <h2>{t("Set up your authenticator")}</h2>
          <p>
            {t(
              "The six digit code is generated on your phone, not sent by this system. There is no server here to send anything.",
            )}
          </p>
          <ol className="gsteps2">
            <li>
              {rich(t("Install {app1}, {app2}, {app3} or {app4} on your phone, if you do not have one."), {
                app1: <b>Google Authenticator</b>,
                app2: <b>Microsoft Authenticator</b>,
                app3: <b>Authy</b>,
                app4: <b>1Password</b>,
              })}
            </li>
            <li>
              {rich(t("Choose {add}, then {scan}, and point the camera at the code below."), {
                add: <b>{t("add account")}</b>,
                scan: <b>{t("scan a QR code")}</b>,
              })}
            </li>
            <li>
              {rich(
                t("No camera? Choose {key} and type the key under the code. Account name: {account}. Type: {type}."),
                {
                  key: <b>{t("enter a setup key")}</b>,
                  account: <b>Sky Logics — {acc?.email}</b>,
                  type: <b>{t("time based")}</b>,
                },
              )}
            </li>
            <li>
              {t("The app then shows a six digit code that changes every thirty seconds. Type the current one here.")}
            </li>
          </ol>
          {pendingSecret && (
            <div className="gnote">
              {t(
                "This account has been through a barcode before, so the app will end up holding two entries with the same name. Delete the older one as soon as the code below is accepted — nothing on the phone says which of the two is live, and the old one is refused.",
              )}
            </div>
          )}
          {acc && (
            <div className="gqrwrap" dir="ltr">
              <AuthenticatorQr email={acc.email} secret={acc.secret} />
            </div>
          )}
          <div className="gsecret">{(acc?.secret.match(/.{1,4}/g) || []).join(" ")}</div>
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <button
              className="btn ghost"
              style={{ margin: 0, padding: 8 }}
              onClick={() =>
                void copy(
                  acc!.secret,
                  t("The key is on the clipboard. Paste it into the app as a setup key."),
                  t("Select the key above and copy it."),
                )
              }
            >
              {t("COPY THE KEY")}
            </button>
            <button
              className="btn ghost"
              style={{ margin: 0, padding: 8 }}
              onClick={() =>
                void copy(
                  otpauthUri(acc!.email, acc!.secret),
                  t("Link copied. Open it on the phone that has the authenticator app."),
                  t("Copy the key instead."),
                )
              }
            >
              {t("COPY THE LINK")}
            </button>
          </div>
          {acc?.plainRecovery && (
            <div className="grec">
              <b>{t("Write these down before you continue")}</b>
              <span>
                {t(
                  "Ten recovery codes. Each works once, instead of a code from the app, if you lose the phone. They are hashed here and cannot be shown again.",
                )}
              </span>
              <div className="grecs">
                {acc.plainRecovery.map((c) => (
                  <i key={c}>{c}</i>
                ))}
              </div>
              <button
                className="btn ghost"
                style={{ margin: "6px 0 0", padding: 7 }}
                onClick={() =>
                  void copy(
                    (acc.plainRecovery || []).join("\n"),
                    t("All ten copied. Put them somewhere that is not this device."),
                    t("Write them down from the list above."),
                  )
                }
              >
                {t("COPY ALL TEN")}
              </button>
            </div>
          )}
          <label>
            {t("The six digits from the app")}
            <input
              type="text"
              ref={focusRef}
              inputMode="numeric"
              maxLength={6}
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={onKey}
            />
          </label>
          {msg && <div className="gmsg">{msg}</div>}
          <button className="btn primary" onClick={() => void doVerify()}>
            {t("VERIFY AND CONTINUE")}
          </button>
          <button className="btn ghost" onClick={doSkip}>
            {t("SKIP THE SECOND FACTOR")}
          </button>
        </div>
      ) : stage === "reset" ? (
        <div className="gcard">
          <div className="eyebrow">{t("Authenticator")}</div>
          <h2>{t("Show the barcode again")}</h2>
          <p>
            {t(
              "For a new phone, or if the app was never set up. Prove it is you with the six digits from the app you have now, or with one of your recovery codes. A new barcode is then shown to scan; the old one stops working once a code from the new one is accepted.",
            )}
          </p>
          <label>
            {t("Current code or recovery code")}
            <input
              type="text"
              ref={focusRef}
              placeholder="000000 / XXXX-XXXX"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={onKey}
            />
          </label>
          {msg && <div className="gmsg">{msg}</div>}
          <button className="btn primary" onClick={() => void doReset()}>
            {t("SHOW THE BARCODE")}
          </button>
          <button
            className="btn ghost"
            onClick={() => {
              setStage("forgot");
              setPass("");
              setCode("");
              setMsg("");
            }}
          >
            {t("THE APP IS GONE — START THE AUTHENTICATOR AGAIN")}
          </button>
          <button
            className="btn ghost"
            onClick={() => {
              setStage("code");
              setCode("");
              setMsg("");
            }}
          >
            {t("BACK TO THE CODE")}
          </button>
        </div>
      ) : stage === "forgot" ? (
        <div className="gcard">
          <div className="eyebrow">{t("Authenticator")}</div>
          <h2>{t("Start the authenticator again")}</h2>
          <p>
            {t(
              "For when the app is gone and the recovery codes with it. Type the password for this account and a new barcode is shown.",
            )}
          </p>
          <p style={{ marginTop: -8 }}>
            {t(
              "Said plainly: this asks again for something already accepted one screen ago, so it is as strong as the password and no stronger. It is here because the alternative was an account that could never be signed into again.",
            )}
          </p>
          <label>
            {t("Password")}
            <input
              type="password"
              ref={focusRef}
              autoComplete="current-password"
              value={pass}
              onChange={(e) => setPass(e.target.value)}
              onKeyDown={onKey}
            />
          </label>
          {msg && <div className="gmsg">{msg}</div>}
          <button className="btn primary" onClick={() => void doForgot()}>
            {t("SHOW A NEW BARCODE")}
          </button>
          <button
            className="btn ghost"
            onClick={() => {
              setStage("code");
              setPass("");
              setMsg("");
            }}
          >
            {t("BACK TO THE CODE")}
          </button>
          <div className="gsteps">
            <i className="done">{t("1 Credentials")}</i>
            <i className="on">{t("2 Authenticator")}</i>
            <i>{t("3 Device")}</i>
          </div>
        </div>
      ) : stage === "recovery" ? (
        <div className="gcard">
          <div className="eyebrow">{t("Recovery")}</div>
          <h2>{t("Use a recovery code")}</h2>
          <p>
            {t("One of the ten codes written down when the account was set up. Each works once, and is then spent.")}
          </p>
          <label>
            {t("Recovery code")}
            <input
              type="text"
              ref={focusRef}
              placeholder="XXXX-XXXX"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={onKey}
            />
          </label>
          {msg && <div className="gmsg">{msg}</div>}
          <button className="btn primary" onClick={() => void doRecovery()}>
            {t("SIGN IN")}
          </button>
          <button
            className="btn ghost"
            onClick={() => {
              setStage("code");
              setMsg("");
            }}
          >
            {t("BACK TO THE CODE")}
          </button>
        </div>
      ) : (
        <div className="gcard">
          <div className="eyebrow">{t("Second factor")}</div>
          <h2>{t("Enter the code")}</h2>
          <p>
            {rich(t("The six digits from your authenticator app for {email}."), {
              email: <b>{acc ? acc.email : ""}</b>,
            })}
          </p>
          <p style={{ marginTop: -8 }}>
            {rich(t("Open your authenticator app and read the code for {issuer}. It changes every thirty seconds."), {
              issuer: <b>Sky Logics</b>,
            })}
          </p>
          <label>
            {t("Code")}
            <input
              type="text"
              ref={focusRef}
              inputMode="numeric"
              maxLength={6}
              placeholder="000000"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={onKey}
            />
          </label>
          {msg && <div className="gmsg">{msg}</div>}
          <button className="btn primary" onClick={() => void doVerify()}>
            {t("SIGN IN")}
          </button>
          <button
            className="btn ghost"
            onClick={() => {
              setStage("recovery");
              setMsg("");
            }}
          >
            {t("USE A RECOVERY CODE")}
          </button>
          <button
            className="btn ghost"
            onClick={() => {
              setStage("reset");
              setCode("");
              setMsg("");
            }}
          >
            {t("SHOW THE BARCODE AGAIN (NEW PHONE)")}
          </button>
          <button
            className="btn ghost"
            onClick={() => {
              setStage("forgot");
              setPass("");
              setCode("");
              setMsg("");
            }}
          >
            {t("THE APP IS GONE — START THE AUTHENTICATOR AGAIN")}
          </button>
          <button
            className="btn ghost"
            onClick={() => {
              setStage("credentials");
              setAcc(null);
              setMsg("");
            }}
          >
            {t("START AGAIN")}
          </button>
          <div className="gsteps">
            <i className="done">{t("1 Credentials")}</i>
            <i className="on">{t("2 Authenticator")}</i>
            <i>{t("3 Device")}</i>
          </div>
        </div>
      )}

      <div className="gcard gsec">
        <div className="inchead">
          <div className="eyebrow">{t("Security check")}</div>
          <span style={{ flex: 1 }}></span>
          <span className={`gscore ${S.bad > 2 ? "bad" : S.bad ? "warn" : "ok"}`}>{S.score}%</span>
        </div>
        <div className="gseclist">
          {S.items.slice(0, 6).map((x) => (
            <div className="gsrow" key={x.k}>
              <span className="incdot" style={{ background: GO[x.state] }}></span>
              <span>
                <b>{x.label}</b>
                <i>{x.value}</i>
              </span>
            </div>
          ))}
        </div>
        <button className="btn ghost" onClick={() => onSecurityPanel?.()}>
          {t("READ THE FULL CHECK")}
        </button>
      </div>
    </GateFrame>
  );
}

/**
 * The cover both doors share: the brand, the artwork and what the platform
 * does on the left, and whatever the door asks for on the right.
 */
export function GateFrame({ children }: { children: ReactNode }) {
  /* The gate holds the page while it is up. */
  useEffect(() => {
    document.body.classList.add("locked");
    return () => document.body.classList.remove("locked");
  }, []);

  return (
    <div id="gate" className="on">
      <div id="gateBody">
        <div className="gwrap">
          <div className="gleft">
            <div className="gbrand">
              <img src="/assets/logo.png" alt="Sky Works Group Holdings" />
            </div>
            <h1>
              SKY LOGICS<span>INTELLIGENCE SYSTEMS</span>
            </h1>
            <div className="gtag">
              {t("Smart Airport Operations Platform — artificial intelligence with a robust predictive model")}
            </div>
            <GateArt />
            <div className="gfeat">
              {FEATURES.map(([a, b]) => (
                <div key={a}>
                  <b>{t(a)}</b>
                  <span>{t(b)}</span>
                </div>
              ))}
            </div>
            <div className="gline">
              {rich(t("Manage your entire airport with the {dashboard}"), {
                dashboard: <b>{t("Sky Logics Smart Dashboard")}</b>,
              })}
            </div>
          </div>

          <div className="gright">{children}</div>
        </div>
      </div>
    </div>
  );
}
